import { Balloon } from "../entities/Balloon";
import { burst, Particle } from "../entities/Particle";
import { Frisbee } from "../entities/Frisbee";
import { Player } from "../entities/Player";
import { tutorialAfterNext, tutorialAfterSkip, type TutorialStep } from "../game/flow";
import { FRISBEE_MAX } from "../game/constants";
import { GameState } from "../game/GameState";
import {
  accuracyPercent,
  aimLengthFor,
  applyBalloonGround,
  applyBalloonPop,
  applyMiss,
  isPiercingActive,
  markSuccessfulShot,
  powerRemainingMs,
  registerShot,
} from "../game/rules";
import type { AudioSystem } from "./AudioSystem";
import { hitBalloons } from "./CollisionSystem";
import type { SaveSystem } from "./SaveSystem";
import { SpawnSystem } from "./SpawnSystem";
import { WorldRenderer } from "./WorldRenderer";
import type { BalloonType, CharacterConfig, MatchState, ScoreEntry, Vec2 } from "../game/types";

const POP_COLORS: Record<BalloonType, string> = {
  blue: "#3d8bff",
  green: "#3ecf6a",
  red: "#ff4b4b",
  gold: "#ffd24a",
};

export type { TutorialStep } from "../game/flow";

export interface FloatText {
  x: number;
  y: number;
  text: string;
  life: number;
}

export class PlayController {
  state: GameState;
  player = new Player();
  balloons: Balloon[] = [];
  frisbees: Frisbee[] = [];
  particles: Particle[] = [];
  floats: FloatText[] = [];
  spawn = new SpawnSystem();
  world: WorldRenderer;
  tutorial: TutorialStep = null;
  private powerWasActive = false;
  private lastPowerRemain = 0;

  constructor(
    difficulty: GameState["match"]["difficulty"],
    private readonly save: SaveSystem,
    private readonly audio: AudioSystem,
    world: WorldRenderer,
    tutorial: boolean,
  ) {
    this.state = new GameState(difficulty);
    this.world = world;
    if (tutorial) this.tutorial = "aim";
  }

  get match(): MatchState {
    return this.state.match;
  }

  get character(): CharacterConfig {
    return this.save.data.character;
  }

  startAim(): void {
    if (this.state.frozen || this.match.gameOver) return;
    this.player.startAim();
  }

  throwAt(point: Vec2): void {
    if (this.state.frozen || this.match.gameOver) return;
    if (this.frisbees.length >= Math.max(1, this.match.frisbees)) return;
    const originX = this.player.hand.x;
    const originY = this.player.hand.y;
    const dirX = point.x - originX;
    const dirY = point.y - originY;
    const piercing = isPiercingActive(this.match, this.state.simTimeMs);
    this.frisbees.push(new Frisbee(originX, originY, dirX, dirY, piercing));
    this.state.match = registerShot(this.match);
    this.player.startThrow();
    this.audio.play("throw");
    if (this.tutorial === "throw" || this.tutorial === "aim") this.advanceTutorial();
  }

  update(dt: number, aiming: boolean, pointer: Vec2): void {
    const acc = this.save.data.accessibility;
    if (!this.state.frozen && !this.match.gameOver) {
      this.state.simTimeMs += dt * 1000;
      this.state.dayTime += dt * 1000;
      this.world.update(dt, acc.reduceMotion);
      this.player.update(dt, this.state.simTimeMs / 1000, acc.reduceMotion);
      if (aiming) this.player.startAim();
      this.updatePowerAudio();
      this.updateBalloons(dt);
      this.updateFrisbees(dt);
      this.spawnBalloons(dt);
    } else {
      this.player.update(dt, this.state.simTimeMs / 1000, acc.reduceMotion);
    }
    if (!acc.reduceMotion || !this.state.frozen) {
      this.particles.forEach((p) => p.update(dt));
      this.particles = this.particles.filter((p) => p.alive);
      this.floats.forEach((f) => {
        f.life -= dt;
        f.y -= 28 * dt;
      });
      this.floats = this.floats.filter((f) => f.life > 0);
    }
    this.state.shake = Math.max(0, this.state.shake - dt * 18);
    void pointer;
  }

  draw(ctx: CanvasRenderingContext2D, aiming: boolean, pointer: Vec2): void {
    const acc = this.save.data.accessibility;
    const shakeX = acc.screenShake ? (Math.random() - 0.5) * this.state.shake : 0;
    const shakeY = acc.screenShake ? (Math.random() - 0.5) * this.state.shake : 0;
    ctx.save();
    ctx.translate(shakeX, shakeY);
    this.world.draw(ctx, this.state.dayTime, acc.reduceMotion, acc.highContrast);
    this.player.draw(ctx, this.character, this.state.simTimeMs / 1000, isPiercingActive(this.match, this.state.simTimeMs), acc.reduceMotion);
    for (const balloon of this.balloons) {
      if (balloon.alive) balloon.draw(ctx, acc.colorAssist, acc.highContrast, acc.reduceMotion);
    }
    for (const frisbee of this.frisbees) frisbee.draw(ctx, frisbee.piercing, acc.highContrast);
    for (const particle of this.particles) particle.draw(ctx);
    for (const f of this.floats) {
      ctx.globalAlpha = Math.max(0, f.life);
      ctx.fillStyle = acc.highContrast ? "#111" : "#fff";
      ctx.font = `700 ${18 * acc.textScale}px Trebuchet MS, sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText(f.text, f.x, f.y);
      ctx.globalAlpha = 1;
    }
    if (aiming && !this.state.frozen && !this.match.gameOver) this.drawAim(ctx, pointer);
    ctx.restore();
  }

  scoreEntry(): ScoreEntry {
    return {
      nickname: this.character.nickname,
      score: this.match.score,
      date: new Date().toISOString(),
      bestCombo: this.match.bestCombo,
      accuracy: accuracyPercent(this.match),
    };
  }

  private drawAim(ctx: CanvasRenderingContext2D, pointer: Vec2): void {
    const len = aimLengthFor(this.match.difficulty, this.save.data.accessibility.aimLengthScale);
    const ox = this.player.hand.x;
    const oy = this.player.hand.y;
    const dx = pointer.x - ox;
    const dy = pointer.y - oy;
    const dist = Math.hypot(dx, dy) || 1;
    const tx = ox + (dx / dist) * len;
    const ty = oy + (dy / dist) * len;
    ctx.setLineDash([10, 10]);
    ctx.strokeStyle = this.save.data.accessibility.highContrast ? "#111" : "rgba(255,255,255,0.9)";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(ox, oy);
    ctx.lineTo(tx, ty);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(tx, ty, 6, 0, Math.PI * 2);
    ctx.fillStyle = "#ffd24a";
    ctx.fill();
  }

  private updateBalloons(dt: number): void {
    const reduceMotion = this.save.data.accessibility.reduceMotion;
    for (const balloon of this.balloons) {
      if (this.tutorial === "aim" || this.tutorial === "throw") {
        balloon.t = Math.min(balloon.t + dt, balloon.duration * 0.48);
        balloon.update(0, reduceMotion);
        continue;
      }
      const grounded = balloon.update(dt, reduceMotion);
      if (grounded) {
        this.state.match = applyBalloonGround(this.match);
        this.audio.play("life");
        if (this.save.data.accessibility.screenShake) this.state.shake = 10;
        if (this.tutorial === "lives") this.advanceTutorial();
      }
    }
    this.balloons = this.balloons.filter((b) => b.alive);
  }

  private updateFrisbees(dt: number): void {
    for (const frisbee of this.frisbees) {
      frisbee.update(dt);
      const hits = hitBalloons(frisbee, this.balloons);
      for (const balloon of hits) {
        if (!balloon.alive || !frisbee.alive) continue;
        if (frisbee.hitCount === 0) this.state.match = markSuccessfulShot(this.match);
        const beforePower = isPiercingActive(this.match, this.state.simTimeMs);
        const result = applyBalloonPop(this.match, balloon.type, this.state.simTimeMs);
        this.state.match = result.state;
        if (frisbee.hitCount === 0) {
          this.player.reactHit(this.match.combo, balloon.type === "gold", this.save.data.accessibility.reduceMotion);
        }
        balloon.pop();
        frisbee.hitCount += 1;
        this.floats.push({ x: balloon.x, y: balloon.y, text: `+${result.points}`, life: 0.8 });
        this.particles.push(
          ...burst(
            balloon.x,
            balloon.y,
            POP_COLORS[balloon.type],
            balloon.type === "gold",
            this.save.data.accessibility.reduceMotion,
          ),
        );
        this.audio.play(balloon.type === "gold" ? "goldPop" : "pop");
        if (this.match.frisbees === FRISBEE_MAX) this.audio.play("reload");
        if (balloon.type === "gold" && !beforePower) this.audio.play("powerStart");
        if (this.tutorial === "reload" || this.tutorial === "combo" || this.tutorial === "gold") this.advanceTutorial();
        if (!frisbee.piercing) {
          frisbee.alive = false;
          break;
        }
      }
      if (frisbee.alive && frisbee.outOfBounds) {
        frisbee.alive = false;
        if (frisbee.hitCount === 0) {
          this.state.match = applyMiss(this.match);
          this.audio.play("miss");
          this.player.reactMiss(this.state.match.frisbees === 0, this.save.data.accessibility.reduceMotion);
        }
      }
    }
    this.frisbees = this.frisbees.filter((f) => f.alive);
  }

  private spawnBalloons(dt: number): void {
    if (this.tutorial && this.tutorial !== "done") {
      this.spawnTutorialBalloon();
      return;
    }
    const spawned = this.spawn.update(dt, this.match, this.balloons);
    if (spawned) this.balloons.push(spawned);
  }

  private spawnTutorialBalloon(): void {
    if (this.balloons.some((b) => b.alive)) return;
    if (this.tutorial === "aim" || this.tutorial === "throw" || this.tutorial === "ammo") {
      this.balloons.push(this.spawn.create("blue", this.match, 720));
    } else if (this.tutorial === "reload" || this.tutorial === "combo") {
      this.balloons.push(this.spawn.create("green", this.match, 760));
    } else if (this.tutorial === "lives") {
      this.balloons.push(this.spawn.create("red", this.match, 820));
    } else if (this.tutorial === "gold") {
      this.balloons.push(this.spawn.create("gold", this.match, 740));
    }
  }

  advanceTutorial(): void {
    this.tutorial = tutorialAfterNext(this.tutorial);
    if (this.tutorial === "done") this.save.markTutorialSeen();
  }

  skipTutorial(): void {
    const result = tutorialAfterSkip();
    this.tutorial = result.tutorial;
    this.save.markTutorialSeen();
    this.balloons = [];
    this.spawn.reset();
  }

  private updatePowerAudio(): void {
    const remain = powerRemainingMs(this.match, this.state.simTimeMs);
    const active = remain > 0;
    if (this.powerWasActive && !active && this.lastPowerRemain > 0) this.audio.play("powerEnd");
    this.powerWasActive = active;
    this.lastPowerRemain = remain;
  }
}

export function powerSeconds(match: MatchState, now: number): number {
  return Math.ceil(powerRemainingMs(match, now) / 1000);
}
